// src/components/features/tournaments/MatchResultForm.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, Trophy, Target, Award, Clock, Calendar, Plus, Minus, Camera, Sparkles, AlertTriangle, Crown, ChevronDown, Upload } from '../../../components/common/Icons';
import { useTranslation } from 'react-i18next';
import { useTournaments } from '../../../context/TournamentContext';
import { useAuth } from '../../../context/AuthContext';
import UserAvatar from '../../common/UserAvatar';
import TeamLogo from '../../common/TeamLogo';
import EnhancedScreenshotUploader from '../../common/EnhancedScreenshotUploader';
import useTournamentPermissions from '../../../hooks/useTournamentPermissions';
import { getSeriesStatus } from '../../../services/database';
import { supabase } from '../../../lib/supabase';
import { AIProcessingService } from '../../../services/aiProcessingService';
import { StorageService } from '../../../services/storageService';
import toast from 'react-hot-toast';

// MVP Selector Component
const MVPSelector = ({ selectedMvpId, players, onMvpChange, formatPlayerName }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  // Get selected player info
  const selectedPlayer = players.find(player => player.player_id === selectedMvpId);

  // Handle dropdown toggle
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-mvp-dropdown]')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle MVP selection
  const handleMvpSelect = (playerId) => {
    onMvpChange(playerId);
    setIsOpen(false);
  };

  return (
    <div className="rounded-xl p-4 border bg-[#1e2129] border-white/[0.07]">
      <h3 className="text-lg font-bold text-white mb-3 flex items-center space-x-2">
        <Award className="w-5 h-5 text-[#23F8B5]" />
        <span>{t('common.mvp_selection_title', 'MVP Selection')}</span>
        <span className="text-sm font-normal text-red-400 ml-2">
          ({t('common.required', 'Required')})
        </span>
      </h3>

      <div className="space-y-3">
        <p className="text-sm text-gray-400">
          {t('common.mvp_selection_description_mandatory', 'You must select the Most Valuable Player from the participating players.')}
        </p>

        {/* MVP Dropdown */}
        <div className="relative" data-mvp-dropdown>
          <button
            type="button"
            onClick={toggleDropdown}
            className="w-full flex items-center justify-between p-3 rounded-lg border border-white/10 hover:border-[#31C590]/50 transition-all duration-200 bg-[#22262f]"
          >
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              {selectedPlayer ? (
                <>
                  <UserAvatar
                    user={{
                      id: selectedPlayer.player_id,
                      avatar_url: selectedPlayer.player_avatar,
                      username: selectedPlayer.player_name,
                      display_name: selectedPlayer.player_name
                    }}
                    size="w-10 h-10"
                    textSize="text-sm"
                    clickable={false}
                  />
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-medium text-white flex items-center gap-1.5">
                      <span className="truncate">{formatPlayerName(selectedPlayer)}</span>
                      <Crown className="w-4 h-4 text-amber-300 flex-shrink-0" />
                    </div>
                    <div className="text-sm text-gray-400">
                      {selectedPlayer.team_name} • {selectedPlayer.goals}G • {selectedPlayer.assists}A • {selectedPlayer.saves}S
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <Award className="w-5 h-5 text-red-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-white">
                      {t('common.select_mvp', 'Select MVP')}
                    </div>
                    <div className="text-sm text-red-400">
                      {t('common.mvp_required_message', 'MVP selection is required')}
                    </div>
                  </div>
                </>
              )}
            </div>
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Options */}
          {isOpen && (
            <div
              className="absolute top-full left-0 right-0 z-50 bg-[#1e2129] border border-white/10 rounded-lg shadow-lg max-h-80 overflow-y-auto mt-1"
              data-mvp-dropdown
            >

              {/* Player Options */}
              {players.map(player => (
                <button
                  key={player.player_id}
                  type="button"
                  onClick={() => handleMvpSelect(player.player_id)}
                  className={`w-full flex items-center space-x-3 p-3 hover:bg-[#22262f] transition-colors duration-200 border-t border-white/[0.05] ${
                    selectedMvpId === player.player_id ? 'bg-[#31C590]/10' : ''
                  }`}
                >
                  <UserAvatar
                    user={{
                      id: player.player_id,
                      avatar_url: player.player_avatar,
                      username: player.player_name,
                      display_name: player.player_name
                    }}
                    size="w-9 h-9"
                    textSize="text-sm"
                    clickable={false}
                  />
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-medium text-white flex items-center gap-1.5">
                      <span className="truncate">{formatPlayerName(player)}</span>
                      {selectedMvpId === player.player_id && <Crown className="w-4 h-4 text-amber-300 flex-shrink-0" />}
                    </div>
                    <div className="text-sm text-gray-400">
                      {player.team_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {player.goals}G • {player.assists}A • {player.saves}S
                    </div>
                  </div>
                  {selectedMvpId === player.player_id && (
                    <div className="w-5 h-5 bg-[#31C590] rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-zinc-900 rounded-full"></div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MatchResultForm = ({ 
  match: originalMatch, 
  onClose, 
  onSave, 
  selectedGame, 
  type = 'tournament', 
  mode = 'admin',
  initialData = null, // NEW: For pre-filling modification data
  modificationMode = null // NEW: 'team1', 'team2', 'unified', or null
}) => {
  // Extract the actual match object (handle both series and match objects)
  const match = useMemo(() => {
    if (originalMatch?.id) {
      // Already a match object
      return originalMatch;
    } else if (originalMatch?.games && originalMatch.games.length > 0) {
      // Series object, extract first game
      return originalMatch.games[0];
    }
    return originalMatch;
  }, [originalMatch]);
  // All hooks must be called at the top level, before any conditional returns
  const { t } = useTranslation();
  const { user } = useAuth();
  const { submitMatchResult, scheduleMatch, submitGameResult, getMatchWithGamesData, updateMatchVodUrls, submitTeamVodUrl, getMatchById, getTournamentById, submitTournamentResult, checkTournamentSubmissionPermissions } = useTournaments();

  // Create tournament object from match for permissions check
  const tournament = match ? { id: match.tournament_id, created_by: match.tournament?.created_by } : null;
  const { canManage: canManageTournament } = useTournamentPermissions(tournament);

  // For scrims, allow any team member to submit results
  // For player submission mode, allow team members to submit
  const canManageMatch = type === 'scrim' ? true : (mode === 'player_submission' ? true : canManageTournament);

  // Get full tournament data to check golden goal setting
  const fullTournament = getTournamentById(match?.tournament_id);
  // Swiss tournaments always require a winner (no ties allowed)
  const isGoldenGoalEnabled = fullTournament?.format === 'swiss' ? true : (fullTournament?.golden_goal_overtime ?? true);
  const [games, setGames] = useState([]);
  const [selectedGameForEdit, setSelectedGameForEdit] = useState(null);
  const [loadingGames, setLoadingGames] = useState(false);

  // Games the admin can record for a BO3/BO5 series: the ones that already exist plus the next
  // playable slot, capped by series progression (so you can't record a game the series never reaches).
  const selectableGames = useMemo(() => {
    if (match.series_type !== 'bo3' && match.series_type !== 'bo5') return [];
    const seriesMax = match.series_type === 'bo5' ? 5 : 3;
    const need = match.games_to_win || (match.series_type === 'bo5' ? 3 : 2);
    const t1w = games.filter(g => g.status === 'completed' && g.winner_id === match.team1?.id).length;
    const t2w = games.filter(g => g.status === 'completed' && g.winner_id === match.team2?.id).length;
    const decided = t1w >= need || t2w >= need;
    const completed = games.filter(g => g.status === 'completed').length;
    const showCount = decided
      ? Math.max(1, games.length)
      : Math.min(seriesMax, Math.max(1, games.length, completed + 1));
    return Array.from({ length: showCount }, (_, i) => games[i] || {
      id: `temp-game-${i + 1}`, game_number: i + 1, status: 'pending', team1_score: 0, team2_score: 0
    });
  }, [games, match.series_type, match.games_to_win, match.team1, match.team2]);
  const [activeTab, setActiveTab] = useState(
    type === 'scrim' ? 'result' : 
    mode === 'player_submission' ? 'result' : // Players start with result tab, not schedule
    'schedule'
  );
  const [scheduleData, setScheduleData] = useState(() => {
    // If match has timezone-aware scheduling, use that; otherwise fall back to legacy fields
    if (match.scheduled_datetime) {
      const scheduledDate = new Date(match.scheduled_datetime);
      return {
        match_date: scheduledDate.toISOString().split('T')[0], // YYYY-MM-DD format
        match_time: scheduledDate.toTimeString().slice(0, 5), // HH:MM format
        existing_scheduled_datetime: match.scheduled_datetime,
        existing_scheduler_timezone: match.scheduler_timezone
      };
    } else {
      return {
        match_date: match.match_date || '',
        match_time: match.match_time || '18:00'
      };
    }
  });
  const [resultData, setResultData] = useState(() => {
    // Use initial data for modification mode, otherwise use match data
    const baseData = initialData || {
      team1_score: match.team1_score || 0,
      team2_score: match.team2_score || 0,
      team1_vod_url: match.team1_vod_url || '',
      team2_vod_url: match.team2_vod_url || '',
      player_stats: [],
      mvp_player_id: match.mvp_player_id || null
    };

    return {
      team1_score: baseData.team1_score || 0,
      team2_score: baseData.team2_score || 0,
      team1_vod_url: baseData.team1_vod_url || '',
      team2_vod_url: baseData.team2_vod_url || '',
      playerStats: baseData.player_stats || [],
      isDnf: false,
      dnfWinnerTeam: null,
      mvpPlayerId: baseData.mvp_player_id || null,
      allowOwnGoals: baseData.allow_own_goals || false
    };
  });

  // Player submission specific state
  const [submissionData, setSubmissionData] = useState({
    notes: '',
    screenshotUrls: {
      team1: '',
      team2: ''
    }
  });

  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [seriesStatus, setSeriesStatus] = useState(null);
  const [, setLoadingSeriesStatus] = useState(false);
  
  // AI Screenshot Processing State
  const [aiProcessingResult, setAiProcessingResult] = useState(null);
  const [showAiResults, setShowAiResults] = useState(false);
  const [aiApplied, setAiApplied] = useState(false);
  
  // Screenshot Upload State
  const [uploadedScreenshot, setUploadedScreenshot] = useState(null);
  const [screenshotUploading, setScreenshotUploading] = useState(false);
  
  const [unmatchedAIPlayers, setUnmatchedAIPlayers] = useState([]);
  const [showPlayerMatching, setShowPlayerMatching] = useState(false);
  
  // Tournament submission permission state
  const [submissionPermissions, setSubmissionPermissions] = useState({ canSubmit: true, reason: '' });
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  
  // Player VOD submission state
  const [playerVodUrl, setPlayerVodUrl] = useState('');
  const [playerTeamInfo, setPlayerTeamInfo] = useState(null);

  // Determine player's team and set current VOD URL for player mode
  useEffect(() => {
    if (mode === 'player_submission' && match && user) {
      // Check which team the user belongs to
      const checkPlayerTeam = async () => {
        try {
          const { data: userTeams, error } = await supabase
            .from('team_members')
            .select('team_id, teams(name)')
            .eq('user_id', user.id)
            .in('team_id', [match.team1_id, match.team2_id]);
          
          if (error) throw error;
          
          if (userTeams && userTeams.length > 0) {
            const userTeam = userTeams[0];
            const isTeam1 = userTeam.team_id === match.team1_id;
            const currentVodUrl = isTeam1 ? match.team1_vod_url : match.team2_vod_url;
            
            setPlayerTeamInfo({
              teamId: userTeam.team_id,
              teamName: userTeam.teams.name,
              isTeam1: isTeam1
            });
            setPlayerVodUrl(currentVodUrl || '');
          }
        } catch (error) {
          console.error('Error checking player team:', error);
        }
      };
      
      checkPlayerTeam();
    }
  }, [mode, match, user]);

  // A stat row exists for every roster member; treat all-zero rows as "did not play".
  const hasPlayedStat = (s) =>
    (s.goals || 0) + (s.assists || 0) + (s.passes || 0) + (s.interceptions || 0) + (s.saves || 0) > 0;

  // Update player stat function
  const updatePlayerStat = useCallback((playerId, stat, value) => {
    setResultData(prev => ({
      ...prev,
      playerStats: prev.playerStats.map(player =>
          player.player_id === playerId
              ? { ...player, [stat]: Math.max(0, value) }
              : player
      )
    }));
  }, []);

  // Load series status to show progress
  const loadSeriesStatus = useCallback(async () => {
    if (!match?.series_id) return;

    try {
      setLoadingSeriesStatus(true);
      const result = await getSeriesStatus(match.series_id);
      if (result.success) {
        setSeriesStatus(result.data);
      }
    } catch (error) {
      console.error('Error loading series status:', error);
    } finally {
      setLoadingSeriesStatus(false);
    }
  }, [match?.series_id]);

  // Load existing match data including player stats
  const loadExistingMatchData = useCallback(async () => {
    if (type === 'scrim') {
      // For scrims, we already have all the data we need from the match prop
      // Initialize with existing scores if available
      if (match.team1_score !== null || match.team2_score !== null) {
        setResultData(prev => ({
          ...prev,
          team1_score: match.team1_score || 0,
          team2_score: match.team2_score || 0
        }));
      }
      
      // Load existing player stats if this is an admin override or has existing stats
      if (match.hasExistingStats && match.player_stats && match.player_stats.length > 0) {
        // Only pre-select players who actually played (non-zero stats); a stat row
        // exists for every roster member, so all-zero rows are treated as "did not play".
        const playedStats = match.player_stats.filter(hasPlayedStat);
        const playersWithStats = new Set((playedStats.length ? playedStats : match.player_stats).map(stat => stat.player_id));
        setSelectedPlayers(playersWithStats);

        setResultData(prev => ({
          ...prev,
          playerStats: prev.playerStats.map(player => {
            const existingStat = match.player_stats.find(stat => stat.player_id === player.player_id);
            if (existingStat) {
              return {
                ...player,
                goals: existingStat.goals || 0,
                assists: existingStat.assists || 0,
                passes: existingStat.passes || 0,
                interceptions: existingStat.interceptions || 0,
                saves: existingStat.saves || 0
              };
            }
            return player;
          })
        }));
      }
      return;
    }

    // Handle suggested data for tournament modifications
    if (type === 'tournament' && match.isModification) {
      console.log('🔄 Loading suggested data for tournament modification:', match);
      
      // Pre-fill with suggested scores
      if (match.suggestedTeam1Score !== undefined || match.suggestedTeam2Score !== undefined) {
        console.log('📊 Pre-filling suggested scores:', match.suggestedTeam1Score, 'vs', match.suggestedTeam2Score);
        setResultData(prev => ({
          ...prev,
          team1_score: match.suggestedTeam1Score || 0,
          team2_score: match.suggestedTeam2Score || 0,
          mvp_player_id: match.suggestedMvpId || null,
          team1_vod_url: match.team1_vod_url || '',
          team2_vod_url: match.team2_vod_url || ''
        }));
      }

      // Pre-fill suggested player stats
      if (match.suggestedPlayerStats && match.suggestedPlayerStats.length > 0) {
        console.log('👥 Pre-filling suggested player stats:', match.suggestedPlayerStats);
        const playersWithStats = new Set(match.suggestedPlayerStats.map(stat => stat.player_id));
        setSelectedPlayers(playersWithStats);

        setResultData(prev => ({
          ...prev,
          playerStats: prev.playerStats.map(player => {
            const existingStat = match.suggestedPlayerStats.find(stat => stat.player_id === player.player_id);
            if (existingStat) {
              return {
                ...player,
                goals: existingStat.goals || 0,
                assists: existingStat.assists || 0,
                passes: existingStat.passes || 0,
                interceptions: existingStat.interceptions || 0,
                saves: existingStat.saves || 0
              };
            }
            return player;
          })
        }));
      }
      
      // Pre-fill MVP if available
      if (match.suggestedMvpId) {
        console.log('👑 Pre-filling suggested MVP:', match.suggestedMvpId);
        setResultData(prev => ({
          ...prev,
          mvp_player_id: match.suggestedMvpId
        }));
      }
      
      return; // Exit early for modifications
    }

    // Tournament-specific data loading
    try {
      const result = await getMatchById(match.id);
      if (result.success && result.data) {
        const matchData = result.data;

        // Update team scores if they exist
        if (matchData.team1_score !== null || matchData.team2_score !== null) {
          setResultData(prev => ({
            ...prev,
            team1_score: matchData.team1_score || 0,
            team2_score: matchData.team2_score || 0,
            team1_vod_url: matchData.team1_vod_url || '',
            team2_vod_url: matchData.team2_vod_url || ''
          }));
        }

        // Update scheduling data if available
        if (matchData.scheduled_datetime) {
          // Use timezone-aware scheduling data
          const scheduledDate = new Date(matchData.scheduled_datetime);
          setScheduleData(prev => ({
            ...prev,
            match_date: scheduledDate.toISOString().split('T')[0],
            match_time: scheduledDate.toTimeString().slice(0, 5),
            existing_scheduled_datetime: matchData.scheduled_datetime,
            existing_scheduler_timezone: matchData.scheduler_timezone
          }));
        } else if (matchData.match_date || matchData.match_time) {
          // Fall back to legacy scheduling data
          setScheduleData(prev => ({
            ...prev,
            match_date: matchData.match_date || prev.match_date,
            match_time: matchData.match_time || prev.match_time
          }));
        }

        // Pre-fill player stats if they exist and mark those players as selected
        if (matchData.player_stats && matchData.player_stats.length > 0) {
          // Only pre-select players who actually played (non-zero stats); a stat row
          // exists for every roster member, so all-zero rows are treated as "did not play".
          const playedStats = matchData.player_stats.filter(hasPlayedStat);
          const playersWithStats = new Set((playedStats.length ? playedStats : matchData.player_stats).map(stat => stat.player_id));
          setSelectedPlayers(playersWithStats);

          setResultData(prev => ({
            ...prev,
            playerStats: prev.playerStats.map(player => {
              const existingStat = matchData.player_stats.find(stat => stat.player_id === player.player_id);
              if (existingStat) {
                return {
                  ...player,
                  goals: existingStat.goals || 0,
                  assists: existingStat.assists || 0,
                  passes: existingStat.passes || 0,
                  interceptions: existingStat.interceptions || 0,
                  saves: existingStat.saves || 0
                };
              }
              return player;
            })
          }));
        }
      }
    } catch (error) {
      console.error('Error loading existing match data:', error);
    }
  }, [getMatchById, match, type]);

  // Load games for BO3/BO5 matches (only if no specific game was selected)
  const loadGames = useCallback(async () => {
    // If a specific game was selected (via selectedGame prop), don't load games
    if (selectedGame) {
      setSelectedGameForEdit(match);
      return;
    }

    if (match.series_type !== 'bo3' && match.series_type !== 'bo5') return;

    setLoadingGames(true);
    try {
      const result = await getMatchWithGamesData(match.id);
      if (result.success) {
        setGames(result.data.games || []);
        // Auto-select the first incomplete game or the latest game
        const incompleteGame = result.data.games?.find(g => g.status !== 'completed');
        const latestGame = result.data.games?.[result.data.games.length - 1];
        setSelectedGameForEdit(incompleteGame || latestGame || null);
      }
    } catch (error) {
      console.error('Error loading games:', error);
    } finally {
      setLoadingGames(false);
    }
  }, [getMatchWithGamesData, selectedGame, match]);

  // Initialize modal and prevent body scroll
  useEffect(() => {
    setIsInitialized(true);
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []); // Empty dependency array - only run once on mount

  // Handle ESC key separately
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);


  useEffect(() => {
    const initializeForm = async () => {
      if (!match?.id) {
        console.error('MatchResultForm received invalid match:', match);
        return;
      }

      // Load series status first (tournaments only)
      if (type === 'tournament') {
        await loadSeriesStatus();
      }

      // Initialize player stats for both teams (for all match types)
      const allPlayers = [];

      // For scrims, we already have team data from the match prop
      // For tournaments, if team data doesn't have members, try to fetch the full match details
      let matchWithTeams = match;
      if (type === 'tournament' && (!match.team1?.members || !match.team2?.members)) {
        try {
          const fullMatchResult = await getMatchById(match.id);
          if (fullMatchResult.success && fullMatchResult.data) {
            matchWithTeams = fullMatchResult.data;
          }
        } catch (error) {
          console.error('Error loading full match data:', error);
        }
      }

      // Handle team member data structure differences
      const team1Members = type === 'scrim' ? matchWithTeams.team1?.team_members : matchWithTeams.team1?.members;
      const team2Members = type === 'scrim' ? matchWithTeams.team2?.team_members : matchWithTeams.team2?.members;


      if (team1Members) {
        team1Members.forEach(member => {
          // Handle different user data structures
          const userData = type === 'scrim' ? member.user : member.user;
          allPlayers.push({
            player_id: userData.id,
            team_id: matchWithTeams.team1_id,
            player_name: userData.display_name || userData.username,
            player_gamer_tag: userData.gamer_tag,
            player_avatar: userData.avatar_url,
            team_name: matchWithTeams.team1.name,
            team_logo: matchWithTeams.team1.logo,
            goals: 0,
            assists: 0,
            passes: 0,
            interceptions: 0,
            saves: 0
          });
        });
      }

      if (team2Members) {
        team2Members.forEach(member => {
          // Handle different user data structures
          const userData = type === 'scrim' ? member.user : member.user;
          allPlayers.push({
            player_id: userData.id,
            team_id: matchWithTeams.team2_id,
            player_name: userData.display_name || userData.username,
            player_gamer_tag: userData.gamer_tag,
            player_avatar: userData.avatar_url,
            team_name: matchWithTeams.team2.name,
            team_logo: matchWithTeams.team2.logo,
            goals: 0,
            assists: 0,
            passes: 0,
            interceptions: 0,
            saves: 0
          });
        });
      }

      // Set initial player stats
      setResultData(prev => ({ ...prev, playerStats: allPlayers }));

      // For scrims, pre-select all the selected players from the match
      if (type === 'scrim') {
        const selectedPlayerIds = new Set([
          ...(matchWithTeams.team1_selected_players || []),
          ...(matchWithTeams.team2_selected_players || [])
        ]);
        setSelectedPlayers(selectedPlayerIds);
      }

      // Load existing match data to pre-fill form (after setting initial players)
      await loadExistingMatchData();

      // For BO3/BO5 matches, load games (only if not managing a specific game and tournaments only)
      if (type === 'tournament' && (match.series_type === 'bo3' || match.series_type === 'bo5') && !selectedGame) {
        await loadGames();
      } else if (selectedGame) {
        // If we have a specific game selected, use it directly
        setSelectedGameForEdit(match);
      }
    };

    initializeForm();
  }, [match, getMatchById, loadExistingMatchData, loadGames, loadSeriesStatus, selectedGame, type]);

  // Check tournament submission permissions to prevent duplicate submissions
  useEffect(() => {
    const checkPermissions = async () => {
      if (type !== 'tournament' || mode !== 'player_submission' || !match?.id || !user?.id) {
        setSubmissionPermissions({ canSubmit: true, reason: '' });
        return;
      }

      setLoadingPermissions(true);
      try {
        const result = await checkTournamentSubmissionPermissions(match.id);
        setSubmissionPermissions({
          canSubmit: result.canSubmit,
          reason: result.reason || ''
        });
      } catch (error) {
        console.error('Error checking submission permissions:', error);
        setSubmissionPermissions({ canSubmit: false, reason: 'Failed to check permissions' });
      } finally {
        setLoadingPermissions(false);
      }
    };

    checkPermissions();
  }, [match?.id, user?.id, type, mode, checkTournamentSubmissionPermissions]);

  // Handle BO3/BO5 game result submission
  const handleGameResultSubmit = async () => {
    if (!selectedGameForEdit) {
      toast.error('Please select a game to record results for');
      return;
    }

    // Final validation check
    const validationErrors = getScoreValidation();
    if (validationErrors) {
      toast.error('Please fix the validation issues before submitting');
      return;
    }

    // Show tie confirmation only if golden goal is NOT enabled (Swiss tournaments always have golden goal enabled)
    if (resultData.team1_score === resultData.team2_score && !isGoldenGoalEnabled) {
      if (!window.confirm('This game will end in a tie. Is this correct?')) {
        return;
      }
    }

    setSaving(true);
    try {
      const gameData = {
        gameNumber: selectedGameForEdit.game_number,
        team1_score: resultData.team1_score,
        team2_score: resultData.team2_score,
        isDnf: resultData.isDnf,
        dnfWinnerTeam: resultData.dnfWinnerTeam,
        mvpPlayerId: resultData.isDnf ? null : resultData.mvpPlayerId, // Include MVP selection for series games
        // Only include stats for selected players, reset stats for DNF
        playerStats: resultData.isDnf ? [] : resultData.playerStats.filter(player => selectedPlayers.has(player.player_id))
      };

      const result = await submitGameResult(match.id, gameData);
      if (result.success) {
        toast.success(resultData.isDnf ? `Game ${selectedGameForEdit.game_number} DNF result recorded successfully!` : `Game ${selectedGameForEdit.game_number} result recorded successfully!`);


        await loadGames(); // Reload games to show updated state
        onSave && onSave();
        // Don't close the modal, let user continue with next game
      } else {
        toast.error(result.error || 'Failed to record game result');
      }
    } catch (error) {
      toast.error('An error occurred while recording the result');
    } finally {
      setSaving(false);
    }
  };

  // Permission check after hooks but before main component logic
  if (!canManageMatch) {
    return null;
  }

  const calculateTeamGoals = (teamId) => {
    return resultData.playerStats
    .filter(player => player.team_id === teamId && selectedPlayers.has(player.player_id))
    .reduce((sum, player) => sum + (player.goals || 0), 0);
  };

  const calculateTeamAssists = (teamId) => {
    return resultData.playerStats
    .filter(player => player.team_id === teamId && selectedPlayers.has(player.player_id))
    .reduce((sum, player) => sum + (player.assists || 0), 0);
  };

  const togglePlayerSelection = (playerId) => {
    setSelectedPlayers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(playerId)) {
        newSet.delete(playerId);
        // Reset player stats when deselected
        setResultData(prevData => ({
          ...prevData,
          playerStats: prevData.playerStats.map(player =>
            player.player_id === playerId
              ? { ...player, goals: 0, assists: 0, passes: 0, interceptions: 0, saves: 0 }
              : player
          )
        }));
      } else {
        newSet.add(playerId);
      }
      return newSet;
    });
  };

  // Helper function to format player name with gamer tag
  const formatPlayerName = (player) => {
    if (player.player_gamer_tag) {
      return `${player.player_name} (${player.player_gamer_tag})`;
    }
    return player.player_name;
  };

  // Score validation function
  const getScoreValidation = () => {
    // Skip validation for DNF matches
    if (resultData.isDnf) {
      return null;
    }

    const errors = [];

    // Check if at least one player is selected
    if (selectedPlayers.size === 0) {
      errors.push('At least one player must be selected to record statistics');
    }

    // Check if MVP is selected (mandatory for tournaments)
    if (type === 'tournament' && selectedPlayers.size > 0 && !resultData.mvpPlayerId) {
      errors.push('MVP selection is required for tournament matches');
    }

    // Check for ties when golden goal is enabled
    if (isGoldenGoalEnabled && resultData.team1_score === resultData.team2_score && resultData.team1_score > 0) {
      if (fullTournament?.format === 'swiss') {
        errors.push('Ties are not allowed in Swiss tournaments - every match must have a winner');
      } else {
        errors.push('Ties are not allowed when Golden Goal (overtime) is enabled');
      }
    }

    const team1Goals = calculateTeamGoals(match.team1_id);
    const team2Goals = calculateTeamGoals(match.team2_id);
    const team1Assists = calculateTeamAssists(match.team1_id);
    const team2Assists = calculateTeamAssists(match.team2_id);

    // Check if player goals match team score (skip if own goals are allowed)
    if (!resultData.allowOwnGoals) {
      if (team1Goals !== resultData.team1_score) {
        errors.push(`${match.team1?.name} player goals (${team1Goals}) don't match team score (${resultData.team1_score})`);
      }

      if (team2Goals !== resultData.team2_score) {
        errors.push(`${match.team2?.name} player goals (${team2Goals}) don't match team score (${resultData.team2_score})`);
      }
    }

    // Check if assists don't exceed goals (can't have more assists than goals)
    if (team1Assists > team1Goals) {
      errors.push(`${match.team1?.name} has more assists (${team1Assists}) than goals (${team1Goals})`);
    }

    if (team2Assists > team2Goals) {
      errors.push(`${match.team2?.name} has more assists (${team2Assists}) than goals (${team2Goals})`);
    }

    return errors.length > 0 ? errors : null;
  };

  // Enhanced Screenshot Processing Handler with Upload
  const handleScreenshotProcessing = async (file, aiResult) => {
    try {
      setScreenshotUploading(true);
      
      // Determine team number for screenshot upload
      // For tournament matches, determine based on team permissions
      // For scrims, determine based on user's team membership
      let teamNumber = 1; // Default to team 1
      
      if (user && match) {
        // Check if user belongs to team2
        const userTeam2Member = match.team2?.members?.some(member => 
          (member.user_id || member.id) === user.id
        );
        if (userTeam2Member) {
          teamNumber = 2;
        }
      }
      
      // Upload screenshot to storage
      const uploadResult = await StorageService.uploadMatchScreenshot(
        match.id,
        file,
        teamNumber,
        user?.id
      );
      
      // Store screenshot info
      setUploadedScreenshot({
        ...uploadResult,
        file,
        teamNumber
      });
      
      // Update submissionData with screenshot URL
      setSubmissionData(prev => ({
        ...prev,
        screenshotUrls: {
          ...prev.screenshotUrls,
          [teamNumber === 1 ? 'team1' : 'team2']: uploadResult.url
        }
      }));
      
      // Process AI results
      setAiProcessingResult(aiResult);
      setShowAiResults(true);
      
      // Auto-apply AI results if they are valid
      if (aiResult && aiResult.success) {
        applyAIResultsInternal(aiResult);
        setAiApplied(true);
        
        const summary = AIProcessingService.generateProcessingSummary(aiResult);
        toast.success(`${summary.message} Screenshot uploaded and results applied automatically!`);
      } else {
        setAiApplied(false);
        const summary = AIProcessingService.generateProcessingSummary(aiResult);
        toast.error(`Screenshot uploaded but AI processing failed: ${summary.message}`);
      }
      
    } catch (uploadError) {
      console.error('Screenshot upload error:', uploadError);
      toast.error(`Screenshot upload failed: ${uploadError.message}`);
      
      // Still show AI results and auto-apply even if upload failed
      setAiProcessingResult(aiResult);
      setShowAiResults(true);
      
      if (aiResult && aiResult.success) {
        applyAIResultsInternal(aiResult);
        setAiApplied(true);
      } else {
        setAiApplied(false);
      }
    } finally {
      setScreenshotUploading(false);
    }
  };

  // AI Screenshot Processing Handlers
  const handleAIProcessingComplete = (result, file = null) => {
    if (file) {
      // Enhanced processing with screenshot upload
      handleScreenshotProcessing(file, result);
    } else {
      // Legacy processing without upload
      setAiProcessingResult(result);
      setShowAiResults(true);
      
      // Auto-apply results if they are valid
      if (result && result.success) {
        // Apply results automatically
        applyAIResultsInternal(result);
        setAiApplied(true);
        
        const summary = AIProcessingService.generateProcessingSummary(result);
        toast.success(`${summary.message} - Results applied automatically!`);
      } else {
        setAiApplied(false);
        const summary = AIProcessingService.generateProcessingSummary(result);
        toast.error(summary.message);
      }
    }
  };

  const handleAIProcessingError = (error) => {
    console.error('AI Processing Error:', error);
    toast.error(`AI processing failed: ${error.message || 'Unknown error'}`);
    setAiProcessingResult(null);
    setShowAiResults(false);
    setUploadedScreenshot(null); // Clear any upload state
    setScreenshotUploading(false);
  };

  // Internal function to apply AI results without user interaction
  const applyAIResultsInternal = (result) => {
    if (!result || !result.success) {
      return;
    }

    try {
      console.log('🚀 Auto-applying AI results...');
      
      // Use enhanced merge with intelligent fallback
      const enhancedResult = AIProcessingService.enhancedMergeWithFallback(
        result.players,
        resultData.playerStats
      );
      
      const { mergedPlayers, unmatchedAI } = enhancedResult;
      
      // Update form data
      setResultData(prev => {
        // Find MVP player ID if AI detected one
        let mvpPlayerId = prev.mvpPlayerId; // Keep current MVP if no AI suggestion
        if (result.mvpPlayer) {
          const mvpFormPlayer = mergedPlayers.find(player => 
            player.player_name.toLowerCase() === result.mvpPlayer.toLowerCase() ||
            player.player_gamer_tag?.toLowerCase() === result.mvpPlayer.toLowerCase()
          );
          if (mvpFormPlayer) {
            mvpPlayerId = mvpFormPlayer.player_id;
            console.log('🏆 MVP auto-assigned:', result.mvpPlayer, '→', mvpFormPlayer.player_name);
          } else {
            console.warn('⚠️ MVP not found in form players:', result.mvpPlayer);
          }
        }
        
        return {
          ...prev,
          playerStats: mergedPlayers,
          mvpPlayerId, // Apply AI-detected MVP
          // Apply extracted scores if available
          ...(result.extractedScores && {
            team1_score: result.extractedScores.homeScore || result.extractedScores.team1Score || 0,
            team2_score: result.extractedScores.awayScore || result.extractedScores.team2Score || 0
          })
        };
      });
      
      // Auto-select players who have stats from AI
      const playersWithAIStats = new Set();
      mergedPlayers.forEach(player => {
        if (player.goals > 0 || player.assists > 0 || player.saves > 0 || player.passes > 0 || player.interceptions > 0) {
          playersWithAIStats.add(player.player_id);
        }
      });
      
      setSelectedPlayers(playersWithAIStats);
      setUnmatchedAIPlayers(unmatchedAI);
      
      // Show unmatched players section if needed
      if (unmatchedAI.length > 0) {
        setShowPlayerMatching(true);
      }
      
    } catch (error) {
      console.error('Error auto-applying AI results:', error);
    }
  };

  const applyAIResults = () => {
    if (!aiProcessingResult || !aiProcessingResult.success) {
      toast.error('No valid AI results to apply');
      return;
    }
    
    // Use the internal apply function
    applyAIResultsInternal(aiProcessingResult);
    setAiApplied(true);
    
    // Enhanced messaging
    const mvpMessage = aiProcessingResult.mvpPlayer ? ` MVP: ${aiProcessingResult.mvpPlayer}.` : '';
    toast.success(`✅ Re-applied AI results for ${aiProcessingResult.players.length} player(s).${mvpMessage}`);
  };

  const revertAIResults = () => {
    // Reset form to original state before AI application
    setResultData(prev => ({
      ...prev,
      playerStats: prev.playerStats.map(player => ({
        ...player,
        goals: 0,
        assists: 0,
        passes: 0,
        interceptions: 0,
        saves: 0
      })),
      mvpPlayerId: null,
      team1_score: 0,
      team2_score: 0
    }));
    
    setSelectedPlayers(new Set());
    setAiApplied(false);
    setShowPlayerMatching(false);
    setUnmatchedAIPlayers([]);
    
    toast.success('Reverted AI results. Form reset to default values.');
  };

  const clearAIResults = () => {
    setAiProcessingResult(null);
    setShowAiResults(false);
    setAiApplied(false);
    setUnmatchedAIPlayers([]);
    setShowPlayerMatching(false);
  };

  const assignUnmatchedPlayer = (aiPlayer, formPlayerId) => {
    // Find the form player
    const formPlayer = resultData.playerStats.find(p => p.player_id === formPlayerId);
    if (!formPlayer) return;

    // Update the form player with AI stats
    setResultData(prev => ({
      ...prev,
      playerStats: prev.playerStats.map(player =>
        player.player_id === formPlayerId
          ? {
              ...player,
              goals: aiPlayer.stats.goals || 0,
              assists: aiPlayer.stats.assists || 0,
              passes: aiPlayer.stats.passes || 0,
              interceptions: aiPlayer.stats.interceptions || 0,
              saves: aiPlayer.stats.saves || 0
            }
          : player
      )
    }));

    // Add player to selected players
    setSelectedPlayers(prev => new Set([...prev, formPlayerId]));

    // Remove from unmatched list
    setUnmatchedAIPlayers(prev => prev.filter(p => p.playerName !== aiPlayer.playerName));

    toast.success(`Assigned ${aiPlayer.playerName} stats to ${formPlayer.player_name}`);
  };

  const getMatchContextForAI = () => {
    return {
      team1Name: match.team1?.name || 'Team 1',
      team2Name: match.team2?.name || 'Team 2',
      expectedPlayers: [
        ...((type === 'scrim' ? match.team1?.team_members : match.team1?.members) || []).map(m => 
          (type === 'scrim' ? m.user : m.user).display_name || (type === 'scrim' ? m.user : m.user).username
        ),
        ...((type === 'scrim' ? match.team2?.team_members : match.team2?.members) || []).map(m => 
          (type === 'scrim' ? m.user : m.user).display_name || (type === 'scrim' ? m.user : m.user).username
        )
      ],
      gameMode: type === 'scrim' ? match.game_mode : '3v3'
    };
  };

  const handleScheduleSubmit = async () => {
    if (!scheduleData.match_date || !scheduleData.match_time) {
      toast.error('Please select both date and time');
      return;
    }

    setSaving(true);
    try {
      // Parse the date and time components
      const [year, month, day] = scheduleData.match_date.split('-').map(Number);
      const [hours, minutes] = scheduleData.match_time.split(':').map(Number);
      
      // Create date using individual components to ensure local timezone
      const localDateTime = new Date(year, month - 1, day, hours, minutes, 0);
      
      // Convert to ISO string (UTC) - this handles DST automatically
      const scheduledDatetime = localDateTime.toISOString();
      const schedulerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      console.log('Scheduling match:', {
        input: `${scheduleData.match_date} ${scheduleData.match_time}`,
        localDateTime: localDateTime.toString(),
        utc: scheduledDatetime,
        timezone: schedulerTimezone
      });
      
      const enhancedScheduleData = {
        ...scheduleData,
        scheduled_datetime: scheduledDatetime,
        scheduler_timezone: schedulerTimezone
      };
      
      const result = await scheduleMatch(match.id, enhancedScheduleData);
      if (result.success) {
        // Update local state to reflect the new scheduling
        setScheduleData(prev => ({
          ...prev,
          existing_scheduled_datetime: scheduledDatetime,
          existing_scheduler_timezone: schedulerTimezone
        }));
        toast.success('Match scheduled successfully!');
        onSave && onSave();
      } else {
        toast.error(result.error || 'Failed to schedule match');
      }
    } catch (error) {
      toast.error('An error occurred while scheduling the match');
    } finally {
      setSaving(false);
    }
  };

  const handleResultSubmit = async () => {
    // Final validation check
    const validationErrors = getScoreValidation();
    if (validationErrors) {
      toast.error('Please fix the validation issues before submitting');
      return;
    }

    // Show tie confirmation only if golden goal is NOT enabled (Swiss tournaments always have golden goal enabled)
    if (resultData.team1_score === resultData.team2_score && !isGoldenGoalEnabled && type === 'tournament') {
      if (!window.confirm('This match will end in a tie. Is this correct?')) {
        return;
      }
    }

    setSaving(true);
    try {
      let result;

      if (mode === 'player_submission') {
        // Player submission for tournament approval
        // Determine user's team ID
        const userTeamId = getUserTeamId();
        
        // Prepare match data for submission
        const matchData = {
          team1_score: resultData.team1_score,
          team2_score: resultData.team2_score,
          player_stats: resultData.playerStats.filter(player => selectedPlayers.has(player.player_id)), // Use snake_case to match backend
          mvp_player_id: resultData.mvpPlayerId, // Convert to snake_case for database
          team1_screenshot_url: submissionData.screenshotUrls.team1,
          team2_screenshot_url: submissionData.screenshotUrls.team2,
          isDnf: resultData.isDnf,
          dnfWinnerTeam: resultData.dnfWinnerTeam
        };

        const submissionPayload = {
          matchData,
          notes: submissionData.notes
        };

        result = await submitTournamentResult(match.id, userTeamId, submissionPayload);
      } else {
        // Tournament-specific submission (admin mode)
        const submitData = {
          ...resultData,
          isDnf: resultData.isDnf,
          dnfWinnerTeam: resultData.dnfWinnerTeam,
          mvp_player_id: resultData.isDnf ? null : resultData.mvpPlayerId, // Use snake_case for consistency
          // Only include stats for selected players, reset stats for DNF
          player_stats: resultData.isDnf ? [] : resultData.playerStats.filter(player => selectedPlayers.has(player.player_id))
        };

        result = await submitMatchResult(match.id, submitData);

        // MVP is now selected directly in the form, no separate voting needed
      }

      if (result.success) {
        const successMessage = type === 'scrim'
          ? 'Results submitted! Awaiting confirmation from opposing team.'
          : mode === 'player_submission'
          ? 'Results submitted! Awaiting tournament owner approval.'
          : (resultData.isDnf ? 'DNF result recorded successfully!' : 'Match result recorded successfully!');

        toast.success(successMessage);
        
        // For scrims, show additional info about confirmation deadline
        if (type === 'scrim' && result.data?.confirmation_deadline) {
          const deadline = new Date(result.data.confirmation_deadline);
          const hours = Math.round((deadline - new Date()) / (1000 * 60 * 60));
          setTimeout(() => {
            toast(`⏱️ Opposing team has ${hours} hours to confirm results.`, {
              duration: 5000,
              icon: 'ℹ️',
              style: {
                background: '#3b82f6',
                color: '#ffffff',
              },
            });
          }, 1000);
        }

        onSave && onSave(match.id, resultData);
        onClose();
      } else {
        toast.error(result.error || 'Failed to record match result');
      }
    } catch (error) {
      console.error('Error submitting match result:', error);
      toast.error('An error occurred while recording the result');
    } finally {
      setSaving(false);
    }
  };

  // Helper function to determine user's team ID
  const getUserTeamId = () => {
    if (!user || !match) return null;
    
    // Get registered teams for this tournament that the user is part of
    const tournament = getTournamentById(match.tournament_id);
    if (!tournament?.registeredTeamsDetails) return null;
    
    const userTeams = tournament.registeredTeamsDetails.filter(team => {
      return team.members?.some(member => 
        member.user_id === user.id || member.id === user.id
      );
    }).map(team => team.id);
    
    // Return the team ID that matches one of the teams in this match
    if (userTeams.includes(match.team1_id)) return match.team1_id;
    if (userTeams.includes(match.team2_id)) return match.team2_id;
    
    return null;
  };

  // Reusable stat input component to reduce code duplication
  const StatInput = React.memo(({ player, statType, colorClass, cell = false }) => {
    const [localValue, setLocalValue] = useState(player[statType]);
    const [isFocused, setIsFocused] = useState(false);

    // Update local value when player stat changes from external source
    const playerStatValue = player[statType];
    useEffect(() => {
      if (!isFocused) {
        setLocalValue(playerStatValue);
      }
    }, [playerStatValue, isFocused]);

    const handleInputChange = (e) => {
      const value = e.target.value === '' ? '' : (parseInt(e.target.value) || 0);
      setLocalValue(value);
      // Don't update global state while typing - only on blur
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
      // Only update global state when user finishes editing
      const finalValue = localValue === '' ? 0 : (parseInt(localValue) || 0);
      updatePlayerStat(player.player_id, statType, Math.max(0, finalValue));
    };

    // Compact table-cell variant: a single number input, no label, no +/- buttons
    if (cell) {
      const disabled = type === 'tournament' && resultData.isDnf;
      return (
        <input
          type="number"
          min="0"
          value={localValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          className={`w-full h-8 text-center font-bold text-sm rounded-md border transition-colors ${
            disabled
              ? 'bg-[#191c23] text-gray-600 border-white/[0.05] cursor-not-allowed'
              : `bg-[#191c23] ${colorClass} border-white/10 focus:outline-none focus:ring-2 focus:ring-[#31C590] focus:border-transparent`
          }`}
        />
      );
    }

    return (
      <div className="text-center">
        <div className={`text-sm font-semibold ${colorClass} mb-2`}>
          {t(`profile.${statType}`)}
        </div>
        <div className="flex items-center justify-center space-x-1">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const newValue = Math.max(0, player[statType] - 1);
              setLocalValue(newValue);
              updatePlayerStat(player.player_id, statType, newValue);
            }}
            disabled={type === 'tournament' && resultData.isDnf}
            className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold transition-colors ${
              (type === 'tournament' && resultData.isDnf)
                ? 'bg-white/[0.06] cursor-not-allowed text-gray-600'
                : 'bg-red-500 hover:bg-red-600 text-white'
            }`}
          >
            −
          </button>
          <input
            type="number"
            min="0"
            value={localValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={type === 'tournament' && resultData.isDnf}
            className={`w-12 h-8 text-center font-bold text-lg border rounded-lg transition-colors ${
              (type === 'tournament' && resultData.isDnf)
                ? 'bg-[#191c23] text-gray-600 border-white/10 cursor-not-allowed'
                : 'bg-[#22262f] text-white border-white/10 focus:outline-none focus:ring-2 focus:ring-[#31C590]'
            }`}
          />
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const newValue = player[statType] + 1;
              setLocalValue(newValue);
              updatePlayerStat(player.player_id, statType, newValue);
            }}
            disabled={type === 'tournament' && resultData.isDnf}
            className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold transition-colors ${
              (type === 'tournament' && resultData.isDnf)
                ? 'bg-white/[0.06] cursor-not-allowed text-gray-600'
                : 'bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900'
            }`}
          >
            +
          </button>
        </div>
      </div>
    );
  });

  const updateTeamScore = (team, score) => {
    setResultData(prev => ({
      ...prev,
      [`${team}_score`]: Math.max(0, parseInt(score) || 0)
    }));
  };

  const handleDnfToggle = (isDnfEnabled) => {
    if (isDnfEnabled) {
      // Enable DNF mode - reset all player stats to 0 and clear selections
      setSelectedPlayers(new Set());
      setResultData(prev => ({
        ...prev,
        isDnf: true,
        dnfWinnerTeam: null,
        team1_score: 0,
        team2_score: 0,
        mvpPlayerId: null, // Clear MVP selection for DNF
        playerStats: prev.playerStats.map(player => ({
          ...player,
          goals: 0,
          assists: 0,
          passes: 0,
          interceptions: 0,
          saves: 0
        }))
      }));
    } else {
      // Disable DNF mode
      setResultData(prev => ({
        ...prev,
        isDnf: false,
        dnfWinnerTeam: null,
        team1_score: 0,
        team2_score: 0
      }));
    }
  };

  const handleDnfWinnerSelection = (teamId) => {
    setResultData(prev => ({
      ...prev,
      dnfWinnerTeam: teamId,
      team1_score: teamId === match.team1_id ? 3 : 0,
      team2_score: teamId === match.team2_id ? 3 : 0,
      // Ensure all player stats remain at 0 for DNF
      playerStats: prev.playerStats.map(player => ({
        ...player,
        goals: 0,
        assists: 0,
        passes: 0,
        interceptions: 0,
        saves: 0
      }))
    }));
  };

  // Compact editable stats table for one team's selected players
  const TeamStatsEditColumn = ({ team, teamId, teamScore }) => {
    const players = resultData.playerStats.filter(
      p => p.team_id === teamId && selectedPlayers.has(p.player_id)
    );
    const teamGoals = calculateTeamGoals(teamId);
    const teamAssists = calculateTeamAssists(teamId);
    const isValid = teamGoals === teamScore && teamAssists <= teamGoals;

    return (
      <div className="space-y-3">
        {/* Team validation header */}
        <div className={`flex items-center space-x-3 p-3 rounded-lg border ${
            isValid ? 'bg-[#31C590]/10 border-[#31C590]/30' : 'bg-red-500/10 border-red-500/30'
        }`}>
          <TeamLogo team={team} size="w-10 h-10" textSize="text-2xl" rounded="rounded-lg" />
          <div className="min-w-0">
            <h4 className="text-base font-bold text-white truncate">{team?.name}</h4>
            <div className="text-xs text-gray-400">
              Goals: {teamGoals} · Assists: {teamAssists} · Score: {teamScore}
            </div>
          </div>
          {isValid && <div className="text-[#23F8B5] text-sm font-semibold ml-auto">✓</div>}
        </div>

        {players.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            No players selected for {team?.name}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/[0.07] bg-[#1e2129]">
            <table className="w-full table-fixed text-sm min-w-[440px]">
              <thead>
                <tr className="text-[10px] font-bold uppercase tracking-wide text-gray-500 border-b border-white/10">
                  <th className="text-left py-2.5 px-3">Player</th>
                  <th className="text-center px-1 w-14 text-[#23F8B5]">G</th>
                  <th className="text-center px-1 w-14 text-[#31C590]">A</th>
                  <th className="text-center px-1 w-14 text-gray-300">P</th>
                  <th className="text-center px-1 w-14 text-amber-300">Int</th>
                  <th className="text-center px-1 w-14 text-red-400">Sv</th>
                </tr>
              </thead>
              <tbody>
                {players.map(player => (
                  <tr key={player.player_id} className="border-b border-white/[0.05] last:border-b-0">
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <UserAvatar
                          user={{ id: player.player_id, avatar_url: player.player_avatar, username: player.player_name, display_name: player.player_name }}
                          size="w-6 h-6"
                          textSize="text-[10px]"
                          clickable={false}
                        />
                        <div className="min-w-0 leading-tight">
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="font-semibold text-white text-sm truncate">{player.player_name}</span>
                            {resultData.mvpPlayerId === player.player_id && <Crown className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />}
                          </div>
                          {player.player_gamer_tag && (
                            <div className="text-[11px] text-gray-500 truncate">{player.player_gamer_tag}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-1 py-1.5"><StatInput player={player} statType="goals" colorClass="text-[#23F8B5]" cell /></td>
                    <td className="px-1 py-1.5"><StatInput player={player} statType="assists" colorClass="text-[#31C590]" cell /></td>
                    <td className="px-1 py-1.5"><StatInput player={player} statType="passes" colorClass="text-gray-200" cell /></td>
                    <td className="px-1 py-1.5"><StatInput player={player} statType="interceptions" colorClass="text-amber-300" cell /></td>
                    <td className="px-1 py-1.5"><StatInput player={player} statType="saves" colorClass="text-red-400" cell /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return createPortal(
      <div className="fixed inset-0 bg-black/70 z-50 backdrop-blur-sm">
        <div
          className="fixed top-1/2 left-1/2 bg-[#1e2129] rounded-xl sm:rounded-2xl w-[96%] sm:w-[92%] md:w-[88%] lg:w-[85%] xl:w-[80%] max-w-5xl max-h-[calc(100vh-4rem)] overflow-hidden shadow-2xl border border-white/10"
          style={{
            opacity: isInitialized ? 1 : 0,
            transform: `translate(-50%, -50%) ${isInitialized ? 'translateY(0)' : 'translateY(-20px)'}`
          }}
        >
        <div className="overflow-y-auto max-h-[calc(100vh-4rem)] p-3 sm:p-4 md:p-5" style={{ scrollBehavior: 'auto' }}>
          <div className="flex justify-between items-start mb-3 sm:mb-4 md:mb-5">
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-1">
                {type === 'scrim' ? 'Scrim Result' : `Manage ${(match.series_type === 'bo3' || match.series_type === 'bo5') ? `${match.series_type.toUpperCase()} Series` : 'Match'}`}
              </h2>
              <p className="text-gray-400">
                {type === 'scrim'
                  ? `${match.game_mode} • ${new Date(match.created_at).toLocaleDateString()}`
                  : `Round ${match.round_number} - ${(match.series_type === 'bo3' || match.series_type === 'bo5') ? `${match.series_type.toUpperCase()} Series - Match ${match.match_number}` : `Match ${match.match_number}`}`
                }
              </p>
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center space-x-2">
                  <TeamLogo
                    team={match.team1}
                    size="w-10 h-10"
                    textSize="text-2xl"
                    rounded="rounded-lg"
                  />
                  <span className="font-semibold text-[#23F8B5]">{match.team1?.name || t('common.tbd')}</span>
                </div>
                <span className="text-gray-500">vs</span>
                <div className="flex items-center space-x-2">
                  <TeamLogo
                    team={match.team2}
                    size="w-10 h-10"
                    textSize="text-2xl"
                    rounded="rounded-lg"
                  />
                  <span className="font-semibold text-red-400">{match.team2?.name || t('common.tbd')}</span>
                </div>
              </div>

              {/* Dual Submission Status Banner - Tournament Player Mode Only */}
              {type === 'tournament' && mode === 'player_submission' && (
                <div className="mt-4">
                  {/* Show current submission status */}
                  <div className="p-3 rounded-lg border">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        Dual Team Submission - Both teams can submit independently
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Your submission will be compared with the other team's submission for tournament owner review.
                    </p>
                  </div>
                </div>
              )}

              {/* Series Progress Display */}
              {seriesStatus && seriesStatus.series_type !== 'single' && (
                <div className="mt-4 p-4 bg-[#22262f] rounded-lg border border-white/[0.07]">
                  <h3 className="font-semibold text-[#23F8B5] mb-2">
                    {seriesStatus.series_type.toUpperCase()} Series Progress
                  </h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-gray-200">
                        {seriesStatus.team1?.name}: {seriesStatus.team1_wins}
                      </span>
                      <span className="text-gray-500">-</span>
                      <span className="text-gray-200">
                        {seriesStatus.team2?.name}: {seriesStatus.team2_wins}
                      </span>
                    </div>
                    <div className="text-sm text-gray-400">
                      {match.series_type !== 'single' && (
                        <>
                          Game {match.game_number} of {seriesStatus.games.length}
                          {seriesStatus.series_complete && (
                            <span className="ml-2 text-[#23F8B5] font-semibold">
                              (Series Complete!)
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button
                onClick={onClose}
                className="text-gray-400 hover:text-white text-2xl font-bold w-10 h-10 rounded-full hover:bg-white/[0.06] transition-all duration-300"
            >
              <X className="w-6 h-6 mx-auto" />
            </button>
          </div>

          {/* Tabs */}
          {type === 'tournament' ? (
            <div className="flex space-x-1 mb-4 bg-[#191c23] border border-white/10 rounded-xl p-1">
              {/* Schedule tab - only for admins */}
              {mode === 'admin' && (
                <button
                    onClick={() => setActiveTab('schedule')}
                    className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-lg transition-all duration-200 font-semibold ${
                        activeTab === 'schedule'
                            ? 'bg-[#31C590]/20 text-[#23F8B5]'
                            : 'text-gray-300 hover:text-white hover:bg-white/[0.06]'
                    }`}
                >
                  <Calendar className="w-5 h-5" />
                  <span>Schedule Match</span>
                </button>
              )}
              <button
                  onClick={() => setActiveTab('result')}
                  className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-lg transition-all duration-200 font-semibold ${
                      activeTab === 'result'
                          ? 'bg-[#31C590]/20 text-[#23F8B5]'
                          : 'text-gray-300 hover:text-white hover:bg-white/[0.06]'
                  }`}
              >
                <Trophy className="w-5 h-5" />
                <span>{(match.series_type === 'bo3' || match.series_type === 'bo5') ? 'Record Game Result' : 'Record Result'}</span>
              </button>
              <button
                  onClick={() => setActiveTab('vods')}
                  className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-lg transition-all duration-200 font-semibold ${
                      activeTab === 'vods'
                          ? 'bg-[#31C590]/20 text-[#23F8B5]'
                          : 'text-gray-300 hover:text-white hover:bg-white/[0.06]'
                  }`}
              >
                <Target className="w-5 h-5" />
                <span>VOD URLs</span>
              </button>
            </div>
          ) : (
            /* Scrim - No tabs, go directly to result form */
            <div className="mb-8">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                <Trophy className="w-5 h-5 text-[#23F8B5]" />
                <span>Record Scrim Results</span>
              </h3>
            </div>
          )}

          {/* Schedule Tab - Tournament only, admin mode only */}
          {type === 'tournament' && activeTab === 'schedule' && mode === 'admin' && (
              <div>
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <Calendar className="w-5 h-5 text-[#23F8B5]" />
                    <span>Schedule Match</span>
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Match Date</label>
                      <input
                          type="date"
                          value={scheduleData.match_date}
                          onChange={(e) => setScheduleData({...scheduleData, match_date: e.target.value})}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-3 border border-white/10 rounded-xl bg-[#22262f] text-white [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-[#31C590] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2 flex items-center space-x-2">
                        <Clock className="w-4 h-4" />
                        <span>Match Time</span>
                      </label>
                      <input
                          type="time"
                          value={scheduleData.match_time}
                          onChange={(e) => setScheduleData({...scheduleData, match_time: e.target.value})}
                          className="w-full px-4 py-3 border border-white/10 rounded-xl bg-[#22262f] text-white [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-[#31C590] focus:border-transparent"
                      />
                    </div>
                  </div>

                  {scheduleData.match_date && scheduleData.match_time && (() => {
                    // Check if this is displaying an existing scheduled time or a new one
                    if (scheduleData.existing_scheduled_datetime) {
                      // Display existing scheduled time in user's timezone
                      const existingDate = new Date(scheduleData.existing_scheduled_datetime);
                      return (
                        <div className="mt-4 p-3 bg-[#31C590]/10 rounded-lg border border-[#31C590]/30">
                          <p className="text-gray-200">
                            <strong className="text-[#23F8B5]">Currently scheduled for:</strong> {existingDate.toLocaleString()}
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            Your timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                          </p>
                          {scheduleData.existing_scheduler_timezone && (
                            <p className="text-gray-500 text-xs mt-1">
                              (Originally scheduled in: {scheduleData.existing_scheduler_timezone})
                            </p>
                          )}
                        </div>
                      );
                    } else {
                      // Display new scheduling preview
                      const [year, month, day] = scheduleData.match_date.split('-').map(Number);
                      const [hours, minutes] = scheduleData.match_time.split(':').map(Number);
                      const localDateTime = new Date(year, month - 1, day, hours, minutes, 0);
                      
                      return (
                        <div className="mt-4 p-3 bg-[#22262f] rounded-lg border border-white/[0.07]">
                          <p className="text-gray-200">
                            <strong className="text-[#23F8B5]">Will be scheduled for:</strong> {localDateTime.toLocaleString()}
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            Your timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                          </p>
                          <p className="text-gray-500 text-xs mt-1">
                            (Will be stored as UTC: {localDateTime.toISOString()})
                          </p>
                        </div>
                      );
                    }
                  })()}

                  <button
                      onClick={handleScheduleSubmit}
                      disabled={saving || !scheduleData.match_date || !scheduleData.match_time}
                      className="w-full mt-6 bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 py-3 rounded-xl font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
{saving ? 'Scheduling...' : (scheduleData.existing_scheduled_datetime ? 'Update Schedule' : 'Schedule Match')}
                  </button>
              </div>
          )}


          {/* Result Tab - Tournament and Scrim */}
          {(type === 'scrim' || activeTab === 'result') && (
              <div className="space-y-4">
                {/* BO3/BO5 Game Selector — persistent bar to pick which game to record */}
                {type === 'tournament' && (match.series_type === 'bo3' || match.series_type === 'bo5') && !selectedGame && (
                  <div className="bg-[#1e2129] rounded-xl p-3 border border-white/[0.07]">
                    <h3 className="text-sm font-bold text-white mb-3 flex items-center space-x-2">
                      <Target className="w-4 h-4 text-[#23F8B5]" />
                      <span>{match.series_type.toUpperCase()} Series — Select Game</span>
                    </h3>

                    {loadingGames ? (
                      <div className="text-center py-4">
                        <div className="w-6 h-6 border-2 border-[#31C590] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <p className="text-gray-400 text-sm">Loading games...</p>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {selectableGames.map((game) => {
                          const isSel = selectedGameForEdit?.game_number === game.game_number;
                          return (
                            <button
                              key={game.id || game.game_number}
                              onClick={() => {
                                setSelectedGameForEdit(game);
                                setResultData(prev => ({ ...prev, team1_score: game.team1_score || 0, team2_score: game.team2_score || 0 }));
                              }}
                              className={`flex items-center gap-2 rounded-lg border px-3 py-2 transition-all duration-200 ${
                                isSel ? 'border-[#31C590] bg-[#31C590]/10' : 'border-white/10 bg-[#22262f] hover:border-[#31C590]/40'
                              }`}
                            >
                              <span className={`font-bold text-sm ${isSel ? 'text-[#23F8B5]' : 'text-white'}`}>Game {game.game_number}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                                game.status === 'completed' ? 'bg-[#31C590]/20 text-[#23F8B5] border-[#31C590]/40' :
                                game.status === 'live' ? 'bg-red-500/20 text-red-300 border-red-500/40' :
                                'bg-white/[0.06] text-gray-400 border-white/10'
                              }`}>
                                {game.status === 'completed' ? `${game.team1_score}-${game.team2_score}` :
                                 game.status === 'live' ? 'Live' : 'Pending'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {selectedGameForEdit && (
                      <div className="mt-3 flex items-center gap-2 text-sm">
                        <Target className="w-4 h-4 text-[#23F8B5]" />
                        <span className="font-semibold text-white">Recording results for Game {selectedGameForEdit.game_number}</span>
                        {selectedGameForEdit.status === 'completed' && (
                          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded-full">Editing existing result</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {/* DNF Option - Tournament only */}
                {type === 'tournament' && (
                <div className="bg-[#1e2129] rounded-xl p-3 border border-orange-500/20">
                  <h3 className="text-lg font-bold text-white mb-3 flex items-center space-x-2">
                    <Award className="w-5 h-5 text-orange-400" />
                    <span>DNF (Did Not Finish)</span>
                  </h3>
                  <p className="text-gray-400 mb-3 text-sm">
                    Use DNF for forfeit wins, no-shows, or disqualifications. Results in a 3-0 win with no individual player stats awarded.
                  </p>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={resultData.isDnf}
                        onChange={(e) => handleDnfToggle(e.target.checked)}
                        className="w-5 h-5 accent-orange-500 bg-[#22262f] border-white/10 rounded focus:ring-orange-500 focus:ring-2"
                      />
                      <span className="text-white font-semibold">
                        Mark this match as DNF (Did Not Finish)
                      </span>
                    </label>
                  </div>

                  {resultData.isDnf && (
                    <div className="mt-4 p-3 bg-[#22262f] rounded-lg border border-orange-500/20">
                      <h4 className="font-semibold text-white mb-3">Select the winning team:</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          onClick={() => handleDnfWinnerSelection(match.team1_id)}
                          className={`p-3 rounded-lg border transition-all duration-200 ${
                            resultData.dnfWinnerTeam === match.team1_id
                              ? 'border-[#31C590] bg-[#31C590]/10'
                              : 'border-white/10 bg-[#1e2129] hover:border-[#31C590]/40'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <TeamLogo
                              team={match.team1}
                              size="w-12 h-12"
                              textSize="text-3xl"
                              rounded="rounded-lg"
                            />
                            <div className="text-left">
                              <div className="font-bold text-white">{match.team1?.name}</div>
                              <div className="text-sm text-gray-400">
                                {resultData.dnfWinnerTeam === match.team1_id ? 'Winner by DNF (3-0)' : 'Click to select'}
                              </div>
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={() => handleDnfWinnerSelection(match.team2_id)}
                          className={`p-3 rounded-lg border transition-all duration-200 ${
                            resultData.dnfWinnerTeam === match.team2_id
                              ? 'border-[#31C590] bg-[#31C590]/10'
                              : 'border-white/10 bg-[#1e2129] hover:border-[#31C590]/40'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <TeamLogo
                              team={match.team2}
                              size="w-12 h-12"
                              textSize="text-3xl"
                              rounded="rounded-lg"
                            />
                            <div className="text-left">
                              <div className="font-bold text-white">{match.team2?.name}</div>
                              <div className="text-sm text-gray-400">
                                {resultData.dnfWinnerTeam === match.team2_id ? 'Winner by DNF (3-0)' : 'Click to select'}
                              </div>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* AI Screenshot Processing Section */}
                {!resultData.isDnf && (
                  <div className="bg-[#1e2129] rounded-xl p-3 border border-white/[0.07]">
                    <h3 className="text-lg font-bold text-white mb-3 flex items-center space-x-2">
                      <Sparkles className="w-5 h-5 text-[#23F8B5]" />
                      <span>AI Screenshot Processing</span>
                      {aiApplied && (
                        <span className="text-sm bg-[#31C590]/20 text-[#23F8B5] border border-[#31C590]/40 px-2 py-1 rounded-full">
                          Applied
                        </span>
                      )}
                    </h3>
                    <p className="text-gray-400 mb-4 text-sm">
                      Upload a screenshot of the match results to automatically fill in player statistics and scores. The screenshot will also be saved as evidence for the match.
                    </p>

                    <EnhancedScreenshotUploader
                      onProcessingComplete={handleAIProcessingComplete}
                      onProcessingError={handleAIProcessingError}
                      matchContext={getMatchContextForAI()}
                      disabled={saving || screenshotUploading}
                      onUploadComplete={(url) => {
                        // Store the screenshot URL for evidence
                        setSubmissionData(prev => ({
                          ...prev,
                          screenshotUrls: {
                            ...prev.screenshotUrls,
                            team1: url
                          }
                        }));
                      }}
                      onUploadError={(error) => {
                        console.error('Screenshot upload failed:', error);
                      }}
                      matchId={match.id}
                      tournamentId={match.tournament_id}
                    />

                    {/* AI Results Summary */}
                    {showAiResults && aiProcessingResult && (
                      <div className="mt-4 p-3 bg-[#22262f] rounded-xl border border-white/[0.07]">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
                            <Camera className="w-5 h-5 text-[#23F8B5]" />
                            <span>AI Results</span>
                          </h4>
                          <div className="flex space-x-2">
                            {aiApplied ? (
                              <button
                                onClick={revertAIResults}
                                disabled={saving}
                                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                              >
                                Revert Results
                              </button>
                            ) : (
                              <button
                                onClick={applyAIResults}
                                disabled={saving}
                                className="px-4 py-2 bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 rounded-lg font-medium transition-colors disabled:opacity-50"
                              >
                                <Sparkles className="w-4 h-4 inline mr-1" />
                                Re-apply Results
                              </button>
                            )}
                            <button
                              onClick={clearAIResults}
                              className="px-4 py-2 border border-white/10 text-gray-300 rounded-lg hover:bg-white/[0.06] transition-colors"
                            >
                              Clear
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-400">Confidence:</span>
                            <div className="font-semibold text-white">{Math.round(aiProcessingResult.confidence * 100)}%</div>
                          </div>
                          <div>
                            <span className="text-gray-400">Players Found:</span>
                            <div className="font-semibold text-white">{aiProcessingResult.players.length}</div>
                          </div>
                          {aiProcessingResult.extractedScores && (
                            <div className="col-span-2">
                              <span className="text-gray-400">Team Scores:</span>
                              <div className="font-semibold text-white">
                                {aiProcessingResult.extractedScores.team1Score} - {aiProcessingResult.extractedScores.team2Score}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Confidence Warning */}
                        {aiProcessingResult.confidence < 0.7 && (
                          <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                            <div className="flex items-start space-x-2">
                              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
                              <div className="text-sm text-amber-300">
                                <strong>Low confidence detection.</strong> Please review the extracted data carefully.
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Unmatched Players Section */}
                    {showPlayerMatching && unmatchedAIPlayers.length > 0 && (
                      <div className="mt-4 p-3 bg-[#22262f] rounded-xl border border-white/[0.07]">
                        <h4 className="text-lg font-semibold text-white mb-3">
                          Assign Unmatched Players
                        </h4>
                        <p className="text-sm text-gray-400 mb-4">
                          These players were found in the screenshot but couldn't be automatically matched.
                          Select the correct player for each set of statistics:
                        </p>

                        <div className="space-y-4">
                          {unmatchedAIPlayers.map((aiPlayer, index) => (
                            <div key={index} className="bg-[#1e2129] rounded-lg p-3 border border-white/[0.07]">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h5 className="font-medium text-white mb-2">
                                    "{aiPlayer.playerName}" from screenshot
                                  </h5>
                                  <div className="grid grid-cols-3 gap-2 text-xs text-gray-400 mb-3">
                                    <div>Goals: {aiPlayer.stats.goals || 0}</div>
                                    <div>Assists: {aiPlayer.stats.assists || 0}</div>
                                    <div>Saves: {aiPlayer.stats.saves || 0}</div>
                                    <div>Passes: {aiPlayer.stats.passes || 0}</div>
                                    <div>Interceptions: {aiPlayer.stats.interceptions || 0}</div>
                                    <div>Score: {aiPlayer.stats.score || 0}</div>
                                  </div>
                                  <select
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        assignUnmatchedPlayer(aiPlayer, e.target.value);
                                      }
                                    }}
                                    className="w-full px-3 py-2 border border-white/10 rounded-lg bg-[#22262f] text-white [color-scheme:dark]"
                                    defaultValue=""
                                  >
                                    <option value="">Select a player to assign these stats to...</option>
                                    {resultData.playerStats
                                      .filter(player => !selectedPlayers.has(player.player_id))
                                      .map(player => (
                                        <option key={player.player_id} value={player.player_id}>
                                          {formatPlayerName(player)} ({player.team_name})
                                        </option>
                                      ))
                                    }
                                  </select>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Screenshot Evidence Status */}
                {uploadedScreenshot && (
                  <div className="mt-4 p-3 bg-[#31C590]/10 rounded-xl border border-[#31C590]/30">
                    <div className="flex items-center space-x-2 mb-3">
                      <Camera className="w-5 h-5 text-[#23F8B5]" />
                      <h4 className="text-lg font-semibold text-white">
                        Evidence Uploaded
                      </h4>
                    </div>

                    <div className="text-sm">
                      <div>
                        <span className="text-gray-400">Team:</span>
                        <div className="font-semibold text-[#23F8B5]">
                          {(() => {
                            const isAdminSubmission = mode === 'admin' && canManageTournament;
                            if (isAdminSubmission) {
                              return 'Admin Submission';
                            }
                            const teamName = uploadedScreenshot.teamNumber === 1
                              ? (match.team1?.name || 'Team 1')
                              : (match.team2?.name || 'Team 2');
                            return teamName;
                          })()}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 text-xs text-gray-400">
                      This screenshot will be submitted as evidence with your match results.
                    </div>
                  </div>
                )}

            {/* Match Score Input */}
            <div className={`rounded-xl p-3 border ${
                  (type === 'tournament' && resultData.isDnf)
                    ? 'bg-[#191c23] border-white/[0.05] opacity-50'
                    : 'bg-[#1e2129] border-white/[0.07]'
                }`}>
                  <h3 className="text-lg font-bold text-white mb-3 flex items-center space-x-2">
                    <Trophy className={`w-5 h-5 ${(type === 'tournament' && resultData.isDnf) ? 'text-gray-500' : 'text-[#23F8B5]'}`} />
                    <span>
                      {type === 'scrim' ? 'Match Score' :
                        ((match.series_type === 'bo3' || match.series_type === 'bo5') && selectedGameForEdit
                          ? `Game ${selectedGameForEdit.game_number} Score`
                          : 'Match Score'
                        )
                      }
                      {type === 'tournament' && resultData.isDnf && <span className="text-orange-400 ml-2">(Forfeit 3-0)</span>}
                      {type === 'tournament' && isGoldenGoalEnabled && !resultData.isDnf && (
                        <span className="text-amber-400 ml-2 flex items-center space-x-1">
                          <span>⚽</span>
                          <span className="text-sm font-medium">
                            {fullTournament?.format === 'swiss' ? '(Swiss - No Ties)' : '(Golden Goal - No Ties)'}
                          </span>
                        </span>
                      )}
                    </span>
                  </h3>

                  {(() => {
                    const dnf = type === 'tournament' && resultData.isDnf;
                    const stepBtn = (variant) => dnf
                      ? 'bg-white/[0.06] cursor-not-allowed text-gray-600'
                      : variant === 'minus'
                        ? 'bg-red-500 hover:bg-red-600 text-white'
                        : 'bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900';
                    const scoreInput = `w-14 h-10 text-2xl font-bold text-center rounded-lg border focus:outline-none focus:ring-2 ${
                      dnf ? 'bg-[#191c23] text-gray-600 border-white/10 cursor-not-allowed'
                          : 'bg-[#191c23] text-[#23F8B5] border-white/10 focus:ring-[#31C590]'
                    }`;
                    return (
                      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3">
                        {/* Team 1 */}
                        <div className="flex items-center gap-2.5">
                          <TeamLogo team={match.team1} size="w-9 h-9" textSize="text-xl" rounded="rounded-lg" />
                          <div className="text-base font-bold text-white">{match.team1?.name}</div>
                          <div className="flex items-center gap-1.5 ml-1">
                            <button type="button" onClick={() => updateTeamScore('team1', resultData.team1_score - 1)} disabled={dnf}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${stepBtn('minus')}`}>
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <input type="number" min="0" value={resultData.team1_score}
                              onChange={(e) => updateTeamScore('team1', e.target.value)} disabled={dnf} className={scoreInput} />
                            <button type="button" onClick={() => updateTeamScore('team1', resultData.team1_score + 1)} disabled={dnf}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${stepBtn('plus')}`}>
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        <div className="text-2xl font-bold text-gray-600 select-none px-1">–</div>

                        {/* Team 2 (mirrored) */}
                        <div className="flex items-center gap-2.5">
                          <div className="flex items-center gap-1.5 mr-1">
                            <button type="button" onClick={() => updateTeamScore('team2', resultData.team2_score - 1)} disabled={dnf}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${stepBtn('minus')}`}>
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <input type="number" min="0" value={resultData.team2_score}
                              onChange={(e) => updateTeamScore('team2', e.target.value)} disabled={dnf} className={scoreInput} />
                            <button type="button" onClick={() => updateTeamScore('team2', resultData.team2_score + 1)} disabled={dnf}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${stepBtn('plus')}`}>
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="text-base font-bold text-white">{match.team2?.name}</div>
                          <TeamLogo team={match.team2} size="w-9 h-9" textSize="text-xl" rounded="rounded-lg" />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Own Goals Toggle */}
                  <div className="text-center mt-4">
                    <div className="inline-flex items-center space-x-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <input
                        type="checkbox"
                        id="allowOwnGoals"
                        checked={resultData.allowOwnGoals}
                        onChange={(e) => setResultData(prev => ({ ...prev, allowOwnGoals: e.target.checked }))}
                        className="w-4 h-4 accent-amber-500 bg-[#22262f] border-white/10 rounded focus:ring-amber-500 focus:ring-2"
                      />
                      <label htmlFor="allowOwnGoals" className="text-sm font-medium text-amber-200 cursor-pointer">
                        {t('match.allow_own_goals', 'Allow own goals (ignore goal stats mismatch)')}
                      </label>
                      <div className="group relative">
                        <AlertTriangle className="w-4 h-4 text-amber-400 cursor-help" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                          {t('match.own_goals_tooltip', 'Enable this when own goals occurred that are not counted in player statistics')}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Winner Display */}
                  {resultData.team1_score !== resultData.team2_score && (
                      <div className="text-center mt-4">
                        <p className="text-[#23F8B5] font-semibold">
                          Winner: {resultData.team1_score > resultData.team2_score ? match.team1?.name : match.team2?.name}
                        </p>
                      </div>
                  )}

                  {resultData.team1_score === resultData.team2_score && resultData.team1_score > 0 && (
                      <div className="text-center mt-4">
                        {type === 'tournament' && isGoldenGoalEnabled ? (
                          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p className="text-red-400 font-semibold flex items-center justify-center space-x-2">
                              <span>⚠️</span>
                              <span>
                                {fullTournament?.format === 'swiss'
                                  ? 'Ties not allowed - Swiss tournament rules'
                                  : 'Ties not allowed - Golden Goal enabled'
                                }
                              </span>
                            </p>
                          </div>
                        ) : (
                          <p className="text-amber-300 font-semibold">Match Tied</p>
                        )}
                      </div>
                  )}
                </div>

                {/* Player Selection */}
                <div className={`rounded-xl p-3 border ${
                  (type === 'tournament' && resultData.isDnf)
                    ? 'bg-[#191c23] border-white/[0.05] opacity-75'
                    : 'bg-[#1e2129] border-white/[0.07]'
                }`}>
                  <h3 className="text-lg font-bold text-white mb-3 flex items-center space-x-2">
                    <Award className={`w-5 h-5 ${(type === 'tournament' && resultData.isDnf) ? 'text-gray-400' : 'text-[#23F8B5]'}`} />
                    <span>Select Players Who Played</span>
                    {type === 'tournament' && resultData.isDnf && (
                      <span className="text-orange-400 text-sm font-normal ml-2">(Disabled for DNF)</span>
                    )}
                  </h3>
                  <p className="text-gray-400 mb-4 text-sm">
                    Select which players actually participated in this match. Only selected players will have their stats recorded.
                  </p>

                  {type === 'tournament' && resultData.isDnf && (
                    <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                      <div className="text-orange-300 text-sm">
                        🚫 <strong>DNF Match:</strong> Player selection is disabled for forfeit matches.
                      </div>
                    </div>
                  )}

                  {/* Two Team Columns for Player Selection */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                    {/* Team 1 Player Selection */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-3 mb-3">
                        <TeamLogo
                          team={match.team1}
                          size="w-9 h-9"
                          textSize="text-xl"
                          rounded="rounded-lg"
                        />
                        <div>
                          <h4 className="text-base font-bold text-white">{match.team1?.name}</h4>
                          <p className="text-xs text-gray-400">
                            {resultData.playerStats.filter(p => p.team_id === match.team1_id && selectedPlayers.has(p.player_id)).length} of {resultData.playerStats.filter(p => p.team_id === match.team1_id).length} selected
                          </p>
                        </div>
                      </div>
                      {resultData.playerStats
                        .filter(player => player.team_id === match.team1_id)
                        .map(player => (
                          <label key={player.player_id} className={`flex items-center space-x-2.5 py-1.5 px-2.5 rounded-lg border cursor-pointer transition-colors ${
                            selectedPlayers.has(player.player_id)
                              ? 'bg-[#31C590]/10 border-[#31C590]/40'
                              : 'bg-[#22262f] border-white/[0.07] hover:border-[#31C590]/30'
                          } ${(type === 'tournament' && resultData.isDnf) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                              type="checkbox"
                              checked={selectedPlayers.has(player.player_id)}
                              onChange={() => !(type === 'tournament' && resultData.isDnf) && togglePlayerSelection(player.player_id)}
                              disabled={type === 'tournament' && resultData.isDnf}
                              className="w-4 h-4 accent-[#31C590] bg-[#22262f] border-white/10 rounded focus:ring-[#31C590] focus:ring-2 flex-shrink-0"
                            />
                            <UserAvatar
                              user={{
                                id: player.player_id,
                                avatar_url: player.player_avatar,
                                username: player.player_name,
                                display_name: player.player_name
                              }}
                              size="w-7 h-7"
                              textSize="text-xs"
                              clickable={false}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-white text-sm truncate">{formatPlayerName(player)}</div>
                            </div>
                          </label>
                        ))
                      }
                    </div>

                    {/* Team 2 Player Selection */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-3 mb-3">
                        <TeamLogo
                          team={match.team2}
                          size="w-9 h-9"
                          textSize="text-xl"
                          rounded="rounded-lg"
                        />
                        <div>
                          <h4 className="text-base font-bold text-white">{match.team2?.name}</h4>
                          <p className="text-xs text-gray-400">
                            {resultData.playerStats.filter(p => p.team_id === match.team2_id && selectedPlayers.has(p.player_id)).length} of {resultData.playerStats.filter(p => p.team_id === match.team2_id).length} selected
                          </p>
                        </div>
                      </div>
                      {resultData.playerStats
                        .filter(player => player.team_id === match.team2_id)
                        .map(player => (
                          <label key={player.player_id} className={`flex items-center space-x-2.5 py-1.5 px-2.5 rounded-lg border cursor-pointer transition-colors ${
                            selectedPlayers.has(player.player_id)
                              ? 'bg-[#31C590]/10 border-[#31C590]/40'
                              : 'bg-[#22262f] border-white/[0.07] hover:border-[#31C590]/30'
                          } ${(type === 'tournament' && resultData.isDnf) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                              type="checkbox"
                              checked={selectedPlayers.has(player.player_id)}
                              onChange={() => !(type === 'tournament' && resultData.isDnf) && togglePlayerSelection(player.player_id)}
                              disabled={type === 'tournament' && resultData.isDnf}
                              className="w-4 h-4 accent-[#31C590] bg-[#22262f] border-white/10 rounded focus:ring-[#31C590] focus:ring-2 flex-shrink-0"
                            />
                            <UserAvatar
                              user={{
                                id: player.player_id,
                                avatar_url: player.player_avatar,
                                username: player.player_name,
                                display_name: player.player_name
                              }}
                              size="w-7 h-7"
                              textSize="text-xs"
                              clickable={false}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-white text-sm truncate">{formatPlayerName(player)}</div>
                            </div>
                          </label>
                        ))
                      }
                    </div>
                  </div>
                </div>

                {/* Player Statistics */}
                <div className={`rounded-xl p-3 border ${
                  (type === 'tournament' && resultData.isDnf)
                    ? 'bg-[#191c23] border-white/[0.05] opacity-75'
                    : 'bg-[#1e2129] border-white/[0.07]'
                }`}>
                  <h3 className="text-lg font-bold text-white mb-3 flex items-center space-x-2">
                    <Target className={`w-5 h-5 ${(type === 'tournament' && resultData.isDnf) ? 'text-gray-400' : 'text-[#23F8B5]'}`} />
                    <span>Player Statistics</span>
                    {type === 'tournament' && resultData.isDnf && (
                      <span className="text-orange-400 text-sm font-normal ml-2">(Disabled for DNF)</span>
                    )}
                  </h3>

                  {type === 'tournament' && resultData.isDnf && (
                    <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                      <div className="text-orange-300 text-sm">
                        🚫 <strong>DNF Match:</strong> No individual player statistics will be recorded for this forfeit win.
                      </div>
                    </div>
                  )}

                  {!(type === 'tournament' && resultData.isDnf) && selectedPlayers.size === 0 && (
                    <div className="mb-4 p-3 bg-[#22262f] border border-white/[0.07] rounded-lg">
                      <div className="text-gray-300 text-sm">
                        ℹ️ <strong>Select players above</strong> to record their individual statistics for this match.
                      </div>
                    </div>
                  )}

                  {/* Score Validation */}
                  {getScoreValidation() && (
                      <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                       <div className="text-red-300 font-semibold mb-2">⚠️ Score Validation Issues:</div>
                       <div className="text-red-400 text-sm space-y-1">
                          {getScoreValidation().map((error, index) => (
                              <div key={index}>• {error}</div>
                          ))}
                        </div>
                      </div>
                  )}

                  {/* Two Team Columns */}
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
                    <TeamStatsEditColumn team={match.team1} teamId={match.team1_id} teamScore={resultData.team1_score} />
                    <TeamStatsEditColumn team={match.team2} teamId={match.team2_id} teamScore={resultData.team2_score} />
                  </div>
                </div>

                {/* MVP Selection - Only available for tournaments, not scrims */}
                {type === 'tournament' && !resultData.isDnf && selectedPlayers.size > 0 && (
                  <MVPSelector
                    selectedMvpId={resultData.mvpPlayerId}
                    players={resultData.playerStats
                      .filter(player => selectedPlayers.has(player.player_id))
                      .sort((a, b) => {
                        // Sort by team first, then by player name
                        if (a.team_id !== b.team_id) {
                          return a.team_id === match.team1_id ? -1 : 1;
                        }
                        return a.player_name.localeCompare(b.player_name);
                      })}
                    onMvpChange={(playerId) => setResultData(prev => ({ ...prev, mvpPlayerId: playerId }))}
                    formatPlayerName={formatPlayerName}
                  />
                )}

                {/* Player Submission Fields */}
                {mode === 'player_submission' && (
                  <div className="space-y-4 p-4 bg-[#1e2129] rounded-xl border border-white/[0.07]">
                    <h3 className="text-lg font-bold text-white flex items-center space-x-2">
                      <Upload className="w-5 h-5 text-[#23F8B5]" />
                      <span>Evidence & Notes</span>
                    </h3>

                    {/* Screenshot functionality moved to AI section at top */}
                    <div className="space-y-4">

                      {/* Submission Notes */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Additional Notes (Optional)
                        </label>
                        <textarea
                          value={submissionData.notes}
                          onChange={(e) => setSubmissionData(prev => ({ ...prev, notes: e.target.value }))}
                          placeholder="Add any additional context about the match results..."
                          className="w-full px-4 py-2.5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#31C590] focus:border-transparent bg-[#22262f] text-white placeholder-gray-500 resize-none"
                          rows={3}
                          maxLength={500}
                        />
                        <div className="text-xs text-gray-500 mt-1 text-right">
                          {submissionData.notes.length}/500 characters
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                    onClick={
                      type === 'scrim'
                        ? handleResultSubmit
                        : ((match.series_type === 'bo3' || match.series_type === 'bo5') ? handleGameResultSubmit : handleResultSubmit)
                    }
                    disabled={
                      saving ||
                      loadingPermissions ||
                      getScoreValidation() ||
                      (type === 'tournament' && mode === 'player_submission' && !submissionPermissions.canSubmit) ||
                      (type === 'tournament' && (match.series_type === 'bo3' || match.series_type === 'bo5') && !selectedGameForEdit) ||
                      (type === 'tournament' && resultData.isDnf && !resultData.dnfWinnerTeam)
                    }
                    className={`w-full py-3.5 rounded-xl font-bold text-lg transition-all duration-200 flex items-center justify-center space-x-3 ${
                        getScoreValidation() ||
                        (type === 'tournament' && (match.series_type === 'bo3' || match.series_type === 'bo5') && !selectedGameForEdit) ||
                        (type === 'tournament' && resultData.isDnf && !resultData.dnfWinnerTeam)
                            ? 'bg-white/[0.06] cursor-not-allowed text-gray-500'
                            : (type === 'tournament' && resultData.isDnf)
                                ? 'bg-orange-500 hover:bg-orange-600 text-white'
                                : 'bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 disabled:opacity-50 disabled:cursor-not-allowed'
                    }`}
                >
                  <Save className="w-6 h-6" />
                  <span>{
                    saving ? (mode === 'player_submission' ? 'Submitting Result...' : 'Recording Result...') :
                        loadingPermissions ? 'Checking permissions...' :
                        getScoreValidation() ? 'Fix Validation Issues to Continue' :
                        (type === 'tournament' && mode === 'player_submission' && !submissionPermissions.canSubmit) ? submissionPermissions.reason || 'Cannot submit result' :
                        type === 'scrim' ? 'Record Scrim Result' :
                        mode === 'player_submission' ? 'Submit Result for Approval' :
                        (type === 'tournament' && (match.series_type === 'bo3' || match.series_type === 'bo5') && !selectedGameForEdit) ? 'Select a Game First' :
                        (type === 'tournament' && resultData.isDnf && !resultData.dnfWinnerTeam) ? 'Select DNF Winner First' :
                        (type === 'tournament' && resultData.isDnf) ? 'Record DNF Result (3-0 Forfeit)' :
                        (type === 'tournament' && (match.series_type === 'bo3' || match.series_type === 'bo5')) ? `Record Game ${selectedGameForEdit?.game_number || ''} Result` :
                            'Record Match Result'
                  }</span>
                </button>
              </div>
          )}

          {/* VOD URLs Tab - Tournament only */}
          {type === 'tournament' && activeTab === 'vods' && (
              <div>
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <Target className="w-5 h-5 text-[#23F8B5]" />
                    <span>VOD URLs</span>
                  </h3>
                  
                  {mode === 'admin' ? (
                    // Admin view - can manage both teams' VOD URLs
                    <>
                      <p className="text-gray-300 mb-6 text-sm">
                        Add video links (Twitch VODs, YouTube videos, etc.) for each team's perspective of the match.
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-4 lg:gap-6">
                        {/* Team 1 VOD URL */}
                        <div className="space-y-4">
                          <div className="flex items-center space-x-3 mb-4">
                            <TeamLogo
                              team={match.team1}
                              size="w-12 h-12"
                              textSize="text-3xl"
                              rounded="rounded-lg"
                            />
                            <div>
                              <h4 className="text-lg font-bold text-white">{match.team1?.name || 'Team 1'}</h4>
                              <p className="text-sm text-gray-400">VOD URL</p>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-400 mb-2">
                              Video URL (Twitch, YouTube, etc.)
                            </label>
                            <input
                                type="url"
                                value={resultData.team1_vod_url}
                                onChange={(e) => setResultData(prev => ({ ...prev, team1_vod_url: e.target.value }))}
                                placeholder="https://www.twitch.tv/videos/..."
                                className="w-full px-4 py-2.5 border border-white/10 rounded-lg bg-[#22262f] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#31C590] focus:border-transparent"
                            />
                            {resultData.team1_vod_url && (
                              <div className="mt-2">
                                <a
                                  href={resultData.team1_vod_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#23F8B5] hover:text-[#31C590] text-sm flex items-center space-x-1 transition-colors"
                                >
                                  <span>🔗 Preview Link</span>
                                </a>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Team 2 VOD URL */}
                        <div className="space-y-4">
                          <div className="flex items-center space-x-3 mb-4">
                            <TeamLogo
                              team={match.team2}
                              size="w-12 h-12"
                              textSize="text-3xl"
                              rounded="rounded-lg"
                            />
                            <div>
                              <h4 className="text-lg font-bold text-white">{match.team2?.name || 'Team 2'}</h4>
                              <p className="text-sm text-gray-400">VOD URL</p>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-400 mb-2">
                              Video URL (Twitch, YouTube, etc.)
                            </label>
                            <input
                                type="url"
                                value={resultData.team2_vod_url}
                                onChange={(e) => setResultData(prev => ({ ...prev, team2_vod_url: e.target.value }))}
                                placeholder="https://www.youtube.com/watch?v=..."
                                className="w-full px-4 py-2.5 border border-white/10 rounded-lg bg-[#22262f] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#31C590] focus:border-transparent"
                            />
                            {resultData.team2_vod_url && (
                              <div className="mt-2">
                                <a
                                  href={resultData.team2_vod_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#23F8B5] hover:text-[#31C590] text-sm flex items-center space-x-1 transition-colors"
                                >
                                  <span>🔗 Preview Link</span>
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Admin Save VOD URLs Button */}
                      <button
                          onClick={async () => {
                            setSaving(true);
                            try {
                              const result = await updateMatchVodUrls(match.id, {
                                team1_vod_url: resultData.team1_vod_url,
                                team2_vod_url: resultData.team2_vod_url
                              });
                              if (result.success) {
                                toast.success('VOD URLs saved successfully!');
                                onSave && onSave();
                              } else {
                                toast.error(result.error || 'Failed to save VOD URLs');
                              }
                            } catch (error) {
                              toast.error('An error occurred while saving VOD URLs');
                            } finally {
                              setSaving(false);
                            }
                          }}
                          disabled={saving}
                          className="w-full mt-6 bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 py-3 rounded-xl font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3"
                      >
                        <Save className="w-5 h-5" />
                        <span>{saving ? 'Saving VOD URLs...' : 'Save VOD URLs'}</span>
                      </button>
                    </>
                  ) : (
                    // Player view - can only submit VOD for their team
                    <>
                      <p className="text-gray-300 mb-6 text-sm">
                        Submit a video link (Twitch VOD, YouTube video, etc.) from your team's perspective of the match.
                      </p>

                      {playerTeamInfo ? (
                        <div className="space-y-4">
                          <div className="flex items-center space-x-3 mb-4">
                            <TeamLogo
                              team={playerTeamInfo.isTeam1 ? match.team1 : match.team2}
                              size="w-12 h-12"
                              textSize="text-3xl"
                              rounded="rounded-lg"
                            />
                            <div>
                              <h4 className="text-lg font-bold text-white">{playerTeamInfo.teamName}</h4>
                              <p className="text-sm text-gray-400">Your Team's VOD URL</p>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-400 mb-2">
                              Video URL (Twitch, YouTube, etc.)
                            </label>
                            <input
                                type="url"
                                value={playerVodUrl}
                                onChange={(e) => setPlayerVodUrl(e.target.value)}
                                placeholder="https://www.twitch.tv/videos/... or https://www.youtube.com/watch?v=..."
                                className="w-full px-4 py-2.5 border border-white/10 rounded-lg bg-[#22262f] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#31C590] focus:border-transparent"
                            />
                            {playerVodUrl && (
                              <div className="mt-2">
                                <a
                                  href={playerVodUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#23F8B5] hover:text-[#31C590] text-sm flex items-center space-x-1 transition-colors"
                                >
                                  <span>🔗 Preview Link</span>
                                </a>
                              </div>
                            )}
                          </div>

                          {/* Player Save VOD URL Button */}
                          <button
                              onClick={async () => {
                                setSaving(true);
                                try {
                                  const result = await submitTeamVodUrl(match.id, playerVodUrl);
                                  if (result.success) {
                                    toast.success('VOD URL saved successfully!');
                                    onSave && onSave();
                                  } else {
                                    toast.error(result.error || 'Failed to save VOD URL');
                                  }
                                } catch (error) {
                                  console.error('VOD submission error:', error);
                                  toast.error(error.message || 'An error occurred while saving VOD URL');
                                } finally {
                                  setSaving(false);
                                }
                              }}
                              disabled={saving}
                              className="w-full mt-6 bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 py-3 rounded-xl font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3"
                          >
                            <Save className="w-5 h-5" />
                            <span>{saving ? 'Saving VOD URL...' : 'Save VOD URL'}</span>
                          </button>
                        </div>
                      ) : (
                        <div className="text-center text-gray-400 py-8">
                          <p>You must be a member of one of the participating teams to submit a VOD URL.</p>
                        </div>
                      )}
                    </>
                  )}
              </div>
          )}
        </div>
        </div>
      </div>,
      document.body
  );
};

export default MatchResultForm;